function resolveApiBaseUrl() {
  const configuredBaseUrl = import.meta.env.VITE_API_BASE_URL;
  if (configuredBaseUrl) {
    return configuredBaseUrl.replace(/\/$/, '');
  }

  if (import.meta.env.DEV || typeof window === 'undefined') {
    return '/api/v1';
  }

  const productionHosts = new Set(['aegiswebshield.com', 'www.aegiswebshield.com']);
  if (productionHosts.has(window.location.hostname)) {
    return '/api/v1';
  }

  return 'https://aegiswebshield.com/api/v1';
}

const API_BASE_URL = resolveApiBaseUrl();

const TOKEN_KEY = 'aegis_access_token';
const REFRESH_TOKEN_KEY = 'aegis_refresh_token';

export function getStoredToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setStoredTokens({ access_token: accessToken, refresh_token: refreshToken }) {
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

export function clearStoredTokens() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

async function request(path, { method = 'GET', body, token = getStoredToken() } = {}) {
  let response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers: {
        ...(body ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: body ? JSON.stringify(body) : undefined
    });
  } catch {
    throw new Error('API 서버에 연결할 수 없습니다. 잠시 후 다시 시도하거나 접속 주소를 확인하세요.');
  }

  const payload = await response.json().catch(() => null);
  if (!response.ok || payload?.success === false) {
    const message = payload?.error?.message || `API request failed with status ${response.status}`;
    throw new Error(message);
  }

  return payload?.data;
}

export const aegisApi = {
  login: (credentials) => request('/auth/login', { method: 'POST', body: credentials, token: null }),
  register: (account) => request('/auth/register', { method: 'POST', body: account, token: null }),
  me: () => request('/me'),
  securityDashboard: () => request('/dashboard/security'),
  billingUsage: (organizationId) => request(`/billing/usage?organization_id=${encodeURIComponent(organizationId)}`),
  auditLogs: (filters = {}) => {
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) query.set(key, value);
    });
    const queryString = query.toString();
    return request(`/audit-logs${queryString ? `?${queryString}` : ''}`);
  },
  organizations: () => request('/organizations'),
  organizationMembers: (organizationId) => request(`/organizations/${encodeURIComponent(organizationId)}/members`),
  addOrganizationMember: (organizationId, member) =>
    request(`/organizations/${encodeURIComponent(organizationId)}/members`, { method: 'POST', body: member }),
  projects: (organizationId) => request(`/projects${organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : ''}`),
  createProject: (project) => request('/projects', { method: 'POST', body: project }),
  targets: (projectId) => request(`/targets${projectId ? `?project_id=${encodeURIComponent(projectId)}` : ''}`),
  createTarget: (target) => request('/targets', { method: 'POST', body: target }),
  scans: (filters = {}) => {
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) query.set(key, value);
    });
    const queryString = query.toString();
    return request(`/scans${queryString ? `?${queryString}` : ''}`);
  },
  createScan: (scan) => request('/scans', { method: 'POST', body: scan }),
  startScan: (scanId) => request(`/scans/${encodeURIComponent(scanId)}/start`, { method: 'POST' }),
  scanStatus: (scanId) => request(`/scans/${encodeURIComponent(scanId)}/status`),
  scanLogs: (scanId) => request(`/scans/${encodeURIComponent(scanId)}/logs`),
  cancelScan: (scanId) => request(`/scans/${encodeURIComponent(scanId)}/cancel`, { method: 'POST' }),
  findings: (filters = {}) => {
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) query.set(key, value);
    });
    const queryString = query.toString();
    return request(`/findings${queryString ? `?${queryString}` : ''}`);
  },
  finding: (findingId) => request(`/findings/${encodeURIComponent(findingId)}`),
  generateAiAnalysis: (findingId) => request(`/findings/${encodeURIComponent(findingId)}/ai-analysis`, { method: 'POST' }),
  aiAnalysis: (analysisId) => request(`/ai-analyses/${encodeURIComponent(analysisId)}`),
  reports: (filters = {}) => {
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) query.set(key, value);
    });
    const queryString = query.toString();
    return request(`/reports${queryString ? `?${queryString}` : ''}`);
  },
  createReport: (report) => request('/reports', { method: 'POST', body: report }),
  createRecheckReport: (report) => request('/reports/recheck', { method: 'POST', body: report }),
  report: (reportId) => request(`/reports/${encodeURIComponent(reportId)}`)
};
